<script setup>
import { computed, ref, watch } from 'vue'
import { useForm, router } from '@inertiajs/vue3'

const props = defineProps({
  open: { type: Boolean, default: false },
  asset: { type: Object, default: null },
  tenants: { type: Array, default: () => [] },
})
const emit = defineEmits(['update:open'])

const form = useForm({ filename: '', alt: '', tenants: [] })
const confirmingDelete = ref(false)
const deleting = ref(false)

// Checkbox items: pick by slug, show the friendly name.
const tenantItems = computed(() => props.tenants.map((t) => ({ label: t.name, value: t.slug })))

// Re-seed the form each time a different asset is opened in the modal.
watch(
  () => props.asset,
  (asset) => {
    if (!asset) return
    form.defaults({ filename: asset.filename, alt: asset.alt ?? '', tenants: asset.tenants.map((t) => t.slug) })
    form.reset()
    form.clearErrors()
    confirmingDelete.value = false
  },
  { immediate: true },
)

const close = () => emit('update:open', false)

const save = () => {
  form.patch(`/assets/${props.asset.id}`, { preserveScroll: true, onSuccess: close })
}

const destroy = () => {
  deleting.value = true
  router.delete(`/assets/${props.asset.id}`, {
    preserveScroll: true,
    onSuccess: close,
    onFinish: () => (deleting.value = false),
  })
}
</script>

<template>
  <UModal :open="open" title="Edit image" @update:open="emit('update:open', $event)">
    <template #body>
      <div v-if="asset" class="space-y-4">
        <UFormField label="Display name">
          <UInput v-model="form.filename" class="w-full" />
        </UFormField>

        <UFormField label="Alt text" help="Described to screen readers and exposed in the API">
          <UInput v-model="form.alt" class="w-full" placeholder="e.g. Land Rover Defender on snow" />
        </UFormField>

        <UFormField label="Tenants" help="Which CMS instances can use this asset (it can be shared)">
          <UCheckboxGroup v-model="form.tenants" :items="tenantItems" orientation="horizontal" />
          <p v-if="form.errors.tenants" class="mt-1 text-xs text-error">{{ form.errors.tenants }}</p>
        </UFormField>
      </div>
    </template>

    <template #footer>
      <div class="flex w-full items-center justify-between">
        <div v-if="!confirmingDelete">
          <UButton color="error" variant="ghost" icon="i-lucide-trash-2" label="Delete" @click="confirmingDelete = true" />
        </div>
        <div v-else class="flex items-center gap-2">
          <span class="text-sm text-muted">Delete permanently?</span>
          <UButton color="error" variant="solid" label="Delete" :loading="deleting" @click="destroy" />
          <UButton color="neutral" variant="ghost" label="Keep" @click="confirmingDelete = false" />
        </div>

        <div class="flex gap-2">
          <UButton color="neutral" variant="ghost" label="Cancel" @click="close" />
          <UButton
            label="Save"
            icon="i-lucide-check"
            :loading="form.processing"
            :disabled="!form.tenants.length || form.processing"
            @click="save"
          />
        </div>
      </div>
    </template>
  </UModal>
</template>
